import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Edit2, Trash2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

/**
 * Table - Premium table with sorting, pagination, and row selection
 *
 * Props:
 *  - columns: Array<{
 *      header: string,
 *      accessor: string,
 *      sortable?: boolean,
 *      render?: (value, row) => ReactNode,
 *      width?: string,
 *      align?: 'left' | 'center' | 'right',
 *    }>
 *  - data: Array<Object>
 *  - loading: boolean
 *  - onEdit?: (row) => void
 *  - onDelete?: (row) => void
 *  - hideEdit?: boolean
 *  - selectable?: boolean (checkbox row selection)
 *  - selectedRows?: string[] (array of row ids)
 *  - onSelectRows?: (ids: string[]) => void
 *  - rowKey?: string (field to use as key, default 'id')
 *  - emptyText?: string
 *  - className?: string
 *  - caption?: string
 *
 *  Pagination props (pass all to show pagination):
 *  - currentPage: number
 *  - totalPages: number
 *  - totalEntries: number
 *  - itemsPerPage: number
 *  - onPageChange: (page) => void
 *  - pageSizeOptions?: number[]
 *  - onPageSizeChange?: (size) => void
 */
export default function Table({
  columns = [],
  data = [],
  loading = false,
  onEdit,
  onDelete,
  hideEdit = false,
  selectable = false,
  selectedRows = [],
  onSelectRows,
  rowKey = 'id',
  emptyText = 'No records found',
  className = '',
  caption,

  // Pagination
  currentPage,
  totalPages,
  totalEntries,
  itemsPerPage,
  onPageChange,
  pageSizeOptions = [5, 10, 25, 50],
  onPageSizeChange,
}) {
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const hasActions = !!(onEdit || onDelete);
  const hasPagination = currentPage !== undefined && onPageChange;

  // ---------- Sorting ----------
  const handleSort = (col) => {
    if (!col.sortable) return;
    if (sortKey === col.accessor) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(col.accessor);
      setSortDir('asc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortKey) return 0;
    const av = a[sortKey] ?? '';
    const bv = b[sortKey] ?? '';
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // ---------- Row selection ----------
  const allSelected = data.length > 0 && data.every(row => selectedRows.includes(row[rowKey]));
  const someSelected = data.some(row => selectedRows.includes(row[rowKey])) && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      onSelectRows?.(selectedRows.filter(id => !data.some(r => r[rowKey] === id)));
    } else {
      const ids = data.map(r => r[rowKey]);
      onSelectRows?.([...new Set([...selectedRows, ...ids])]);
    }
  };

  const toggleRow = (id) => {
    if (selectedRows.includes(id)) {
      onSelectRows?.(selectedRows.filter(i => i !== id));
    } else {
      onSelectRows?.([...selectedRows, id]);
    }
  };

  // ---------- Sort icon ----------
  const SortIcon = ({ accessor }) => {
    if (sortKey !== accessor) return <ArrowUpDown size={13} className="ui-table-sort-icon idle" />;
    return sortDir === 'asc'
      ? <ArrowUp size={13} className="ui-table-sort-icon active" />
      : <ArrowDown size={13} className="ui-table-sort-icon active" />;
  };

  // ---------- Skeleton rows ----------
  if (loading) {
    return (
      <div className={`ui-table-wrapper ${className}`}>
        <div className="ui-table-scroll">
          <table className="ui-table" aria-busy="true" aria-label="Loading data">
            <thead>
              <tr>
                {selectable && <th className="ui-table-th ui-table-checkbox-cell" />}
                {columns.map((col, i) => (
                  <th key={i} className="ui-table-th">{col.header}</th>
                ))}
                {hasActions && <th className="ui-table-th ui-table-actions-cell">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, ri) => (
                <tr key={ri} className="ui-table-skeleton-row">
                  {selectable && (
                    <td className="ui-table-td ui-table-checkbox-cell">
                      <div className="ui-skeleton ui-skeleton-box" />
                    </td>
                  )}
                  {columns.map((_, ci) => (
                    <td key={ci} className="ui-table-td">
                      <div className="ui-skeleton" style={{ width: `${60 + Math.random() * 30}%` }} />
                    </td>
                  ))}
                  {hasActions && (
                    <td className="ui-table-td ui-table-actions-cell">
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="ui-skeleton ui-skeleton-btn" />
                        <div className="ui-skeleton ui-skeleton-btn" />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className={`ui-table-wrapper ${className}`}>
      <div className="ui-table-scroll">
        <table className="ui-table" aria-label={caption || 'Data table'}>
          {caption && <caption className="ui-table-caption">{caption}</caption>}
          <thead>
            <tr>
              {selectable && (
                <th className="ui-table-th ui-table-checkbox-cell">
                  <label className="ui-table-checkbox-label" aria-label="Select all rows">
                    <input
                      type="checkbox"
                      className="ui-table-checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll}
                    />
                    <span className="ui-table-checkbox-custom" />
                  </label>
                </th>
              )}
              {columns.map((col, i) => (
                <th
                  key={i}
                  className={`ui-table-th ${col.sortable ? 'sortable' : ''} ${sortKey === col.accessor ? 'sorted' : ''}`}
                  style={{ width: col.width, textAlign: col.align || 'left' }}
                  onClick={() => handleSort(col)}
                  aria-sort={sortKey === col.accessor ? sortDir === 'asc' ? 'ascending' : 'descending' : 'none'}
                >
                  <span className="ui-table-th-inner">
                    {col.header}
                    {col.sortable && <SortIcon accessor={col.accessor} />}
                  </span>
                </th>
              ))}
              {hasActions && (
                <th className="ui-table-th ui-table-actions-cell">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0) + (hasActions ? 1 : 0)}
                  className="ui-table-empty-cell"
                >
                  <div className="ui-table-empty">
                    <span className="ui-table-empty-icon">📂</span>
                    <p>{emptyText}</p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row, ri) => {
                const id = row[rowKey];
                const isSelected = selectedRows.includes(id);
                return (
                  <tr
                    key={id ?? ri}
                    className={`ui-table-row ${isSelected ? 'selected' : ''}`}
                  >
                    {selectable && (
                      <td className="ui-table-td ui-table-checkbox-cell">
                        <label className="ui-table-checkbox-label" aria-label={`Select row ${ri + 1}`}>
                          <input
                            type="checkbox"
                            className="ui-table-checkbox"
                            checked={isSelected}
                            onChange={() => toggleRow(id)}
                          />
                          <span className="ui-table-checkbox-custom" />
                        </label>
                      </td>
                    )}
                    {columns.map((col, ci) => {
                      const val = row[col.accessor];
                      const display = col.render
                        ? col.render(val, row)
                        : (val !== undefined && val !== null && String(val).trim() !== '' ? val : '—');
                      return (
                        <td
                          key={ci}
                          className="ui-table-td"
                          style={{ textAlign: col.align || 'left' }}
                        >
                          {display}
                        </td>
                      );
                    })}
                    {hasActions && (
                      <td className="ui-table-td ui-table-actions-cell">
                        <div className="ui-table-actions">
                          {!hideEdit && onEdit && (
                            <button
                              type="button"
                              className="ui-table-action-btn edit"
                              onClick={() => onEdit(row)}
                              title="Edit"
                              aria-label={`Edit row ${ri + 1}`}
                            >
                              <Edit2 size={14} />
                            </button>
                          )}
                          {onDelete && (
                            <button
                              type="button"
                              className="ui-table-action-btn delete"
                              onClick={() => onDelete(row)}
                              title="Delete"
                              aria-label={`Delete row ${ri + 1}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {hasPagination && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalEntries={totalEntries}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={onPageSizeChange}
          selectedCount={selectedRows.length}
        />
      )}
    </div>
  );
}

/**
 * TablePagination - Standalone pagination component
 */
export function TablePagination({
  currentPage = 1,
  totalPages = 1,
  totalEntries = 0,
  itemsPerPage = 10,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
  selectedCount = 0,
}) {
  const start = totalEntries === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const end = Math.min(currentPage * itemsPerPage, totalEntries);

  // Smart page range
  const getPages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    pages.push(1);
    if (currentPage > 4) pages.push('...');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 3) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="ui-pagination">
      <div className="ui-pagination-left">
        {selectedCount > 0 && (
          <span className="ui-pagination-selected">{selectedCount} selected</span>
        )}
        <span className="ui-pagination-info">
          Showing <strong>{start}</strong>–<strong>{end}</strong> of <strong>{totalEntries}</strong>
        </span>
        {onPageSizeChange && pageSizeOptions && (
          <div className="ui-pagination-size">
            <select
              className="ui-pagination-size-select"
              value={itemsPerPage}
              onChange={e => onPageSizeChange(Number(e.target.value))}
              aria-label="Rows per page"
            >
              {pageSizeOptions.map(s => (
                <option key={s} value={s}>{s} per page</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="ui-pagination-right">
        <button
          type="button"
          className="ui-pagination-btn"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          title="First page"
          aria-label="Go to first page"
        >
          <ChevronsLeft size={15} />
        </button>
        <button
          type="button"
          className="ui-pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          title="Previous page"
          aria-label="Go to previous page"
        >
          <ChevronLeft size={15} />
        </button>

        <div className="ui-pagination-pages">
          {getPages().map((p, i) =>
            p === '...' ? (
              <span key={`el-${i}`} className="ui-pagination-ellipsis">…</span>
            ) : (
              <button
                key={p}
                type="button"
                className={`ui-pagination-page ${currentPage === p ? 'active' : ''}`}
                onClick={() => onPageChange(p)}
                aria-current={currentPage === p ? 'page' : undefined}
                aria-label={`Go to page ${p}`}
              >
                {p}
              </button>
            )
          )}
        </div>

        <button
          type="button"
          className="ui-pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
          title="Next page"
          aria-label="Go to next page"
        >
          <ChevronRight size={15} />
        </button>
        <button
          type="button"
          className="ui-pagination-btn"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || totalPages === 0}
          title="Last page"
          aria-label="Go to last page"
        >
          <ChevronsRight size={15} />
        </button>
      </div>
    </div>
  );
}
